import type { ComponentPropsWithoutRef, ElementType } from 'react';

import { tv } from '../../lib/tv';
import { gapClass, type Space } from './space';

export type InlineProps<T extends ElementType = 'div'> = {
  /** Element to render. Defaults to `div`; use `ul`, `nav`, … for semantics. */
  as?: T;
  /**
   * Space between children, as a Figma `space/*` token. Applies to both axes,
   * so wrapped rows are spaced like the items within a row.
   */
  gap?: Space;
  /** Horizontal distribution of children. */
  align?: 'left' | 'center' | 'right' | 'between';
  /** Vertical alignment of children within the row. */
  alignY?: 'top' | 'center' | 'bottom' | 'baseline' | 'stretch';
  /** Let children flow onto more rows instead of overflowing. */
  wrap?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'gap' | 'align' | 'alignY' | 'wrap'>;

const inline = tv({
  base: 'flex flex-row',
  variants: {
    gap: gapClass,
    align: {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
      between: 'justify-between',
    },
    alignY: {
      top: 'items-start',
      center: 'items-center',
      bottom: 'items-end',
      baseline: 'items-baseline',
      stretch: 'items-stretch',
    },
    wrap: { true: 'flex-wrap', false: 'flex-nowrap' },
  },
  defaultVariants: { gap: 2, align: 'left', alignY: 'center', wrap: false },
});

/**
 * Horizontal layout: children in a row with an on-system gap. Equivalent to a
 * horizontal auto-layout frame in Figma (with "wrap" when `wrap` is set).
 */
export function Inline<T extends ElementType = 'div'>({
  as,
  gap,
  align,
  alignY,
  wrap,
  className,
  ...props
}: InlineProps<T>) {
  const Component: ElementType = as ?? 'div';
  return (
    <Component className={inline({ gap, align, alignY, wrap, className })} {...props} />
  );
}
